/**
 * Recovering a microphone or camera the browser revoked mid-call, with no page
 * reload — the shared step behind both the Permissions `change` listener (the
 * grant came back) and the recovery dialog's "Allow" button (the user asked to
 * try now).
 *
 * `reacquireAfterGrant` prompts when the state is `'prompt'` and, on a grant,
 * primes the device and moves the running producer onto the fresh track. If it
 * succeeds we restore the device to the on/off state it had before the
 * revocation (`wasOn`) and clear the recovery, which also closes the dialog. If
 * the browser still refuses it — a hard `'denied'` block cannot be re-prompted
 * from script — we leave recovery armed so the `change` listener finishes the
 * job when the user flips the setting back.
 *
 * Kept out of the store and the hook so the dialog can call exactly the same
 * path without duplicating it.
 */

import { reacquireAfterGrant } from '@/features/devices/services/permission-gate'
import { useDevicesStore } from '@/features/devices/store/devices.store'
import { useMeetingSessionStore } from '../store/meeting-session.store'

export async function recoverDeviceNow(kind: 'audio' | 'video'): Promise<boolean> {
  const live = await reacquireAfterGrant(kind)
  if (!live) return false

  const devices = useDevicesStore.getState()
  // Restore the device to what it was before the revocation. Default to ON: the
  // only way recovery is armed is that the user was using the device or just
  // tried to turn it on.
  const wasOn = devices.permissionRecovery[kind]?.wasOn ?? true
  if (wasOn) {
    const session = useMeetingSessionStore.getState()
    if (kind === 'audio') session.setMicOn(true)
    else session.setCamOn(true)
  }
  devices.endPermissionRecovery(kind)
  return true
}
